import deepEqual from "fast-deep-equal";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useMemo, useState } from "react";
import { createApiResponse } from "@entrilia/shared/api/axiosHelper";
import useFetch from "@entrilia/shared/hooks/useFetch";
import type {
  PreviewInvoiceCardResponse,
  PreviewInvoiceCardRequest,
} from "../../../../api/types/expenseManagementTypes";
import type { FieldIdToValueMap, RelatedObjectsResponse } from "../../../../api/types/objectTypes";
import type { FieldValueLabeler } from "../../../entityFields/helpers";
import type { RelatedObjectValuesState } from "../../../entityFields/relatedObjectValuesState";
import type { ObjectCardEditChecks } from "../../../entityFields/useObjectCardChecks";
import {
  acknowledgeDefaultsAction,
  applyRowDefaultsAction,
  getActionCheckMessages,
  getCellDecorations,
  getAutoFillsOverriddenByEdit,
  getFieldDefaults,
  getFieldsAutoFillTooltips,
  getInvoiceCardPreviewRequest,
  giveUpDefaultsAction,
  initialInvoiceCardPreviewSession,
  rememberAutoFillsAction,
  requestsDefaults,
  type InvoiceCardPreviewConfig,
} from "./invoiceCardPreviewState";

interface Params {
  config: InvoiceCardPreviewConfig | undefined;
  objectId: string;
  storedFieldValues: FieldIdToValueMap;
  fieldValues: FieldIdToValueMap;
  storedRelatedObjects: RelatedObjectsResponse[] | undefined;
  relatedObjectValuesState: RelatedObjectValuesState;
  setRelatedObjectValuesState: Dispatch<SetStateAction<RelatedObjectValuesState>>;
  onFieldDefaults: (fieldDefaults: FieldIdToValueMap) => void;
  onRowDefaults: (fieldIds: string[]) => void;
  onEditChecksChange: (checks: ObjectCardEditChecks) => void;
}

const useInvoiceCardPreview = ({
  config,
  objectId,
  storedFieldValues,
  fieldValues,
  storedRelatedObjects,
  relatedObjectValuesState,
  setRelatedObjectValuesState,
  onFieldDefaults,
  onRowDefaults,
  onEditChecksChange,
}: Params) => {
  const [session, setSession] = useState(initialInvoiceCardPreviewSession);
  const [sentRequest, setSentRequest] = useState<PreviewInvoiceCardRequest>();

  const storedRows = storedRelatedObjects?.find((x) => x.objectType === config?.relatedObjectType)?.relatedObjects;

  const request =
    config && storedRows
      ? getInvoiceCardPreviewRequest(
          config,
          session,
          storedFieldValues,
          fieldValues,
          storedRows,
          relatedObjectValuesState
        )
      : undefined;

  if (!deepEqual(request, sentRequest)) {
    setSentRequest(request);
  }

  const getPreview = useCallback(
    async (signal?: AbortSignal) => {
      if (!config || !sentRequest) {
        return createApiResponse<PreviewInvoiceCardResponse>({
          fieldValues: {},
          defaults: [],
          autoFilled: [],
          checks: [],
        });
      }

      const handlePreviewFailed = () => {
        if (!signal?.aborted) {
          setSession(giveUpDefaultsAction(config, sentRequest));
          onEditChecksChange("unavailable");
        }
      };

      try {
        const response = await config.getPreview(objectId, sentRequest);
        if (!response.success) {
          handlePreviewFailed();
        }
        return response;
      } catch (error) {
        handlePreviewFailed();
        throw error;
      }
    },
    [config, objectId, sentRequest, onEditChecksChange]
  );

  const handlePreviewLoaded = (loadedPreview: PreviewInvoiceCardResponse) => {
    if (!config || !sentRequest || !storedRows) {
      return;
    }

    setSession(acknowledgeDefaultsAction(config, sentRequest, loadedPreview));
    setSession(rememberAutoFillsAction(loadedPreview));
    onEditChecksChange(getActionCheckMessages(loadedPreview.checks));

    const fieldDefaults = getFieldDefaults(sentRequest, loadedPreview, fieldValues);
    if (Object.keys(fieldDefaults).length > 0) {
      onFieldDefaults(fieldDefaults);
    }

    setRelatedObjectValuesState(applyRowDefaultsAction(config, sentRequest, loadedPreview, storedRows));
    onRowDefaults(
      loadedPreview.defaults.filter((d) => d.relatedObjectKey !== undefined && d.value).map((d) => d.fieldId)
    );
  };

  const [lastPreview, previewError] = useFetch(getPreview, {
    disabled: sentRequest === undefined,
    onSetData: handlePreviewLoaded,
  });
  const preview = previewError ? undefined : lastPreview;

  const fieldsAutoFillTooltips = useMemo(
    () => (config && preview ? getFieldsAutoFillTooltips(config, preview, fieldValues) : undefined),
    [config, preview, fieldValues]
  );

  const cellDecorations = useMemo(
    () => (config && preview ? getCellDecorations(config, preview, relatedObjectValuesState) : undefined),
    [config, preview, relatedObjectValuesState]
  );

  const relatedNoOptionsTexts = useMemo(() => config?.getRelatedNoOptionsTexts?.(fieldValues), [config, fieldValues]);

  const reset = useCallback(() => setSession(initialInvoiceCardPreviewSession), []);

  const reportUserChange = useCallback(
    (
      relatedObjectKey: string | undefined,
      oldValues: FieldIdToValueMap,
      newValues: FieldIdToValueMap,
      labelOf: FieldValueLabeler
    ) => {
      const onAutoFillOverridden = config?.onAutoFillOverridden;
      if (!onAutoFillOverridden || !preview) {
        return;
      }

      getAutoFillsOverriddenByEdit(preview.autoFilled, relatedObjectKey, oldValues, newValues).forEach((fill) =>
        onAutoFillOverridden(objectId, fill, {
          from: labelOf(fill.fieldId, fill.value),
          to: labelOf(fill.fieldId, newValues[fill.fieldId]),
        })
      );
    },
    [config, objectId, preview]
  );

  return {
    fieldValues: preview?.fieldValues,
    hasFailed: previewError !== undefined || session.defaultsMissed === true,
    isWaitingForDefaults: !previewError && request !== undefined && requestsDefaults(request),
    fieldsAutoFillTooltips,
    cellDecorations,
    relatedNoOptionsTexts,
    reset,
    reportUserChange,
  };
};

export default useInvoiceCardPreview;
